package com.eladfinish.bible.contest.features.copyOptions.ui

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.expandVertically
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.RadioButton
import androidx.compose.material3.RadioButtonDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.advanced_options
import com.eladfinish.bible.contest.copy_only_highlights
import com.eladfinish.bible.contest.features.copyOptions.CopyOptionsAction
import com.eladfinish.bible.contest.features.copyOptions.CopyOptionsState
import com.eladfinish.bible.contest.handle_god_names
import com.eladfinish.bible.contest.highlights_with_asterisks
import com.eladfinish.bible.contest.highlights_with_brackets
import com.eladfinish.bible.contest.how_to_save_search_results_highlights
import com.eladfinish.bible.contest.include_book_and_chapter
import com.eladfinish.bible.contest.nikud
import com.eladfinish.bible.contest.no_highlights
import com.eladfinish.bible.contest.preview
import com.eladfinish.bible.contest.remove_duplicates
import com.eladfinish.bible.contest.remove_verse_origin
import com.eladfinish.bible.contest.screens.search.ui.HighlightType
import com.eladfinish.bible.contest.searched_expression
import com.eladfinish.bible.contest.sort_results
import com.eladfinish.bible.contest.teamim
import com.eladfinish.bible.contest.ui.common.accentColor
import com.eladfinish.bible.contest.ui.components.CheckboxWithLabel
import com.eladfinish.bible.contest.ui.components.RightToLeftContainer
import com.eladfinish.bible.contest.ui.components.loaders.CircularLoader
import com.eladfinish.bible.contest.ui.components.verticalScrollWithScrollbar
import com.eladfinish.bible.contest.what_to_copy_as_extra
import dev.icerock.moko.resources.compose.stringResource

@Composable
fun CopyOptionsContent(
    state: CopyOptionsState,
    onAction: (CopyOptionsAction) -> Unit,
    modifier: Modifier = Modifier,
) {
    val options = state.options
    val config = state.config

    RightToLeftContainer {
        Column(
            modifier = modifier
                .fillMaxWidth()
                .padding(5.dp)
                .verticalScrollWithScrollbar(),
        ) {
            val alignToStart: Modifier =
                Modifier.align(Alignment.Start)

            Text(
                text = stringResource(SharedRes.strings.what_to_copy_as_extra),
                fontSize = 20.sp,
                modifier = Modifier.align(Alignment.CenterHorizontally),
                textAlign = TextAlign.Center,
                color = Color.DarkGray,
            )

            if (config.shouldShowSearchedExpressionOption) {
                CheckboxWithLabel(
                    checked = options.includeExpression,
                    onCheckedChange = { checked ->
                        onAction(
                            CopyOptionsAction.SetIncludeExpression(
                                value = checked,
                            ),
                        )
                    },
                    text = stringResource(SharedRes.strings.searched_expression),
                    modifier = alignToStart,
                )
            }

            CheckboxWithLabel(
                checked = options.withNikud,
                onCheckedChange = { checked ->
                    onAction(
                        CopyOptionsAction.SetIncludeNikud(
                            value = checked,
                        ),
                    )
                },
                text = stringResource(SharedRes.strings.nikud),
                modifier = alignToStart,
            )

            CheckboxWithLabel(
                checked = options.withTeamim,
                onCheckedChange = { checked ->
                    onAction(
                        CopyOptionsAction.SetIncludeTeamim(
                            value = checked,
                        ),
                    )
                },
                text = stringResource(SharedRes.strings.teamim),
                modifier = alignToStart,
            )

            if (config.shouldShowIncludeBookAndChapterOption) {
                CheckboxWithLabel(
                    checked = options.includeBookAndChapter,
                    onCheckedChange = { checked ->
                        onAction(
                            CopyOptionsAction.SetIncludeBookAndChapter(
                                value = checked,
                            ),
                        )
                    },
                    text = stringResource(SharedRes.strings.include_book_and_chapter),
                    modifier = alignToStart,
                )
            }

            CheckboxWithLabel(
                checked = options.hideGodNames,
                onCheckedChange = { checked ->
                    onAction(
                        CopyOptionsAction.SetHandleGodNames(
                            value = checked,
                        ),
                    )
                },
                text = stringResource(SharedRes.strings.handle_god_names),
                modifier = alignToStart,
            )

            if (config.shouldShowHighlightsSection) {
                HorizontalDivider()

                Text(
                    text = stringResource(SharedRes.strings.how_to_save_search_results_highlights),
                    fontSize = 20.sp,
                    modifier = Modifier.align(Alignment.CenterHorizontally),
                    textAlign = TextAlign.Center,
                    color = Color.DarkGray,
                )

                val highlightOptions = listOf(
                    HighlightType.NONE to SharedRes.strings.no_highlights,
                    HighlightType.BRACKETS to SharedRes.strings.highlights_with_brackets,
                    HighlightType.ASTERISKS to SharedRes.strings.highlights_with_asterisks,
                )

                highlightOptions.forEach { (type, titleRes) ->
                    val isSelected = options.highlightType == type

                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        modifier = Modifier
                            .fillMaxWidth()
                            .selectable(
                                selected = isSelected,
                                onClick = {
                                    onAction(
                                        CopyOptionsAction.SetHighlightType(
                                            value = type,
                                        ),
                                    )
                                },
                            ),
                    ) {
                        RadioButton(
                            selected = isSelected,
                            onClick = {
                                onAction(
                                    CopyOptionsAction.SetHighlightType(
                                        value = type,
                                    ),
                                )
                            },
                            colors = RadioButtonDefaults.colors().copy(
                                selectedColor = accentColor,
                            ),
                        )

                        Text(
                            text = stringResource(titleRes),
                            fontSize = 20.sp,
                        )
                    }
                }
            }

            if (config.shouldShowAdvancedOptionsSection) {
                HorizontalDivider()

                var showAdvancedOptions by rememberSaveable {
                    mutableStateOf(
                        value = false,
                    )
                }

                val arrowUp = "⇧"
                val arrowDown = "⇩"

                val advancedText = stringResource(SharedRes.strings.advanced_options)
                    .plus(" ")
                    .plus(if (showAdvancedOptions) arrowUp else arrowDown)

                Text(
                    text = advancedText,
                    fontSize = 20.sp,
                    modifier = Modifier
                        .align(Alignment.CenterHorizontally)
                        .padding(top = 5.dp)
                        .clickable { showAdvancedOptions = !showAdvancedOptions },
                    textAlign = TextAlign.Center,
                    color = accentColor,
                )

                AnimatedVisibility(
                    visible = showAdvancedOptions,
                    enter = expandVertically(),
                    exit = shrinkVertically(),
                ) {
                    Column {
                        CheckboxWithLabel(
                            checked = options.removeVerseOrigin,
                            onCheckedChange = { checked ->
                                onAction(
                                    CopyOptionsAction.SetRemoveVerseOrigin(
                                        value = checked,
                                    ),
                                )
                            },
                            text = stringResource(SharedRes.strings.remove_verse_origin),
                            modifier = alignToStart,
                        )

                        CheckboxWithLabel(
                            checked = options.copyOnlyHighlights,
                            onCheckedChange = { checked ->
                                onAction(
                                    CopyOptionsAction.SetCopyOnlyHighlights(
                                        value = checked,
                                    ),
                                )
                            },
                            text = stringResource(SharedRes.strings.copy_only_highlights),
                            modifier = alignToStart,
                        )

                        if (config.shouldShowRemoveDuplicatesOption) {
                            CheckboxWithLabel(
                                checked = options.removeDuplicates,
                                onCheckedChange = { checked ->
                                    onAction(
                                        CopyOptionsAction.SetRemoveDuplicates(
                                            value = checked,
                                        ),
                                    )
                                },
                                text = stringResource(SharedRes.strings.remove_duplicates),
                                modifier = alignToStart,
                            )
                        }

                        if (config.shouldShowSortResultOption) {
                            CheckboxWithLabel(
                                checked = options.sortResults,
                                onCheckedChange = { checked ->
                                    onAction(
                                        CopyOptionsAction.SetSortResults(
                                            value = checked,
                                        ),
                                    )
                                },
                                text = stringResource(SharedRes.strings.sort_results),
                                modifier = alignToStart,
                            )
                        }
                    }
                }
            }

            HorizontalDivider()

            Text(
                text = stringResource(SharedRes.strings.preview),
                fontSize = 20.sp,
                modifier = Modifier.align(Alignment.CenterHorizontally),
                color = Color.DarkGray,
            )

            if (state.isLoading) {
                CircularLoader(
                    strokeWidth = 4.dp,
                    modifier = Modifier
                        .size(40.dp)
                        .align(Alignment.CenterHorizontally),
                )
            }

            Card(
                modifier = Modifier
                    .padding(5.dp)
                    .wrapContentWidth()
                    .fillMaxHeight()
                    .align(Alignment.CenterHorizontally),
                elevation = CardDefaults.cardElevation(3.dp),
                shape = RoundedCornerShape(12.dp),
            ) {
                SelectionContainer {
                    Text(
                        text = state.previewText,
                        fontSize = 18.sp,
                        color = MaterialTheme.colorScheme.onSurface,
                        modifier = Modifier
                            .padding(5.dp)
                            .align(Alignment.CenterHorizontally),
                    )
                }
            }
        }
    }
}
